import { GroupSharingT, UserSharingT } from 'business/definitions';
import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import {
    handleChangeEditAccessOf,
    isUserSharing,
    removeUser,
} from '../helpers';

import { Button } from '@mui/material';
import SelectedSharingOption from './SelectedSharingOption';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_NUMBER_OF_LINES_DISPLAYED = 5;

type SelectedOptionsListProps = {
    selectedOptions: (UserSharingT | GroupSharingT)[];
    disabled: boolean;
    setSelectedUsers: Dispatch<SetStateAction<UserSharingT[]>>;
    setSelectedUserGroups: Dispatch<SetStateAction<GroupSharingT[]>>;
};

const SelectedOptionsList = ({
    selectedOptions,
    disabled,
    setSelectedUsers,
    setSelectedUserGroups,
}: SelectedOptionsListProps): React.ReactElement => {
    const { t } = useTranslation();

    const [displayShowAllButton, setDisplayShowAllButton] = useState(
        selectedOptions.length > DEFAULT_MAX_NUMBER_OF_LINES_DISPLAYED,
    );

    const handleSwitchChange = (
        share: UserSharingT | GroupSharingT,
        checked: boolean,
    ) => {
        if (isUserSharing(share)) {
            handleChangeEditAccessOf(checked, share.key, setSelectedUsers);
            return;
        }
        setSelectedUserGroups((oldState) =>
            oldState.map((userGroupShare) => {
                if (userGroupShare.key === share.key)
                    return {
                        ...userGroupShare,
                        editAccess: checked,
                    };
                return userGroupShare;
            }),
        );
    };

    const handleDeleteClick = (share: UserSharingT | GroupSharingT) => {
        if (
            displayShowAllButton &&
            selectedOptions.length - 1 === DEFAULT_MAX_NUMBER_OF_LINES_DISPLAYED
        )
            setDisplayShowAllButton(false);
        if (isUserSharing(share)) {
            removeUser(share.key, setSelectedUsers);
            return;
        }
        setSelectedUserGroups((oldState) =>
            oldState.filter(
                (userGroupShare) => userGroupShare.key !== share.key,
            ),
        );
    };

    const sharingsToRender = displayShowAllButton
        ? selectedOptions.slice(0, DEFAULT_MAX_NUMBER_OF_LINES_DISPLAYED)
        : selectedOptions;
    return (
        <>
            {sharingsToRender.map((share) => (
                <SelectedSharingOption
                    key={`sharing-option-${share.key}`}
                    disabled={disabled}
                    share={share}
                    onDeleteClick={() => handleDeleteClick(share)}
                    handleSwitchChange={(
                        event: ChangeEvent<HTMLInputElement>,
                    ) => handleSwitchChange(share, event.target.checked)}
                />
            ))}
            {displayShowAllButton && (
                <Button
                    variant="text"
                    onClick={() => setDisplayShowAllButton(false)}
                    size="small"
                >
                    {`${t('sharingModal.seeMore')} (+${selectedOptions.length - DEFAULT_MAX_NUMBER_OF_LINES_DISPLAYED})`}
                </Button>
            )}
        </>
    );
};

export default SelectedOptionsList;
